/**
 * src/pages/Shop.jsx
 * Clean path-based URLs for gender/category/subcategory (better for SEO
 * sitelinks and shareable links) — /shop/girls/ethnic-wear/lehenga.
 * Age stays a query param (?age=...) since it's a multi-select refinement,
 * not a natural single path segment.
 *
 * On mobile, filters live behind a "Filters" button that opens a bottom
 * sheet with Category/Type/Age together and an Apply button.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ProductCard } from "../components/ProductCard";
import { fetchProducts, fetchFacets, fetchAvailableSizeLabels } from "../lib/supabaseProducts";
import { slugify, findBySlug } from "../lib/slug";

const GENDERS = [
  { value: "", label: "All" },
  { value: "girls", label: "Girls" },
  { value: "boys", label: "Boys" },
  { value: "unisex", label: "Unisex" },
];

export default function Shop() {
  // gender/category/subcategory come from the path, e.g. /shop/girls/ethnic-wear/lehenga
  const { gender = "", categorySlug, subcategorySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedSizes, setSelectedSizes] = useState(() => {
    const a = searchParams.get("age");
    return a ? a.split(",") : [];
  });
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  const [facets, setFacets] = useState({ categories: [], subcategoriesByCategory: {} });
  const [sizeLabels, setSizeLabels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve the URL's category/subcategory slugs back to real facet values
  const selectedCategory = useMemo(() => findBySlug(facets.categories, categorySlug), [facets.categories, categorySlug]);
  const availableSubcategories = useMemo(() => {
    if (!selectedCategory) return [];
    return facets.subcategoriesByCategory[selectedCategory] || [];
  }, [facets, selectedCategory]);
  const selectedSubcategory = useMemo(
    () => findBySlug(availableSubcategories, subcategorySlug),
    [availableSubcategories, subcategorySlug]
  );

  useEffect(() => {
    fetchFacets({ gender: gender || undefined }).then(setFacets);
    fetchAvailableSizeLabels().then(setSizeLabels);
  }, [gender]);

  useEffect(() => {
    setLoading(true);
    fetchProducts({
      gender: gender || undefined,
      categories: selectedCategory ? [selectedCategory] : undefined,
      subcategories: selectedSubcategory ? [selectedSubcategory] : undefined,
      sizeLabels: selectedSizes.length > 0 ? selectedSizes : undefined,
    }).then((data) => {
      setProducts(data);
      setLoading(false);
    });
  }, [gender, selectedCategory, selectedSubcategory, selectedSizes]);

  // Age stays in the query string, kept in sync as it changes
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    selectedSizes.length ? next.set("age", selectedSizes.join(",")) : next.delete("age");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSizes]);

  function toggleSize(value) {
    setSelectedSizes((arr) => (arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]));
  }

  function pathFor(g, catSlug, subSlug) {
    let path = "/shop";
    if (g) path += `/${g}`;
    if (catSlug) path += `/${catSlug}`;
    if (subSlug) path += `/${subSlug}`;
    return path + (searchParams.get("age") ? `?age=${searchParams.get("age")}` : "");
  }

  const activeFilterCount = (selectedCategory ? 1 : 0) + (selectedSubcategory ? 1 : 0) + selectedSizes.length;

  const filterGroups = (
    <>
      {facets.categories.length > 0 && (
        <div className="filter-block">
          <div className="filter-title">Category</div>
          <Link to={pathFor(gender)} className={`filter-option ${!selectedCategory ? "filter-option-active" : ""}`}>
            All
          </Link>
          {facets.categories.map((cat) => (
            <Link
              key={cat}
              to={pathFor(gender, slugify(cat))}
              className={`filter-option ${selectedCategory === cat ? "filter-option-active" : ""}`}
            >
              {cat}
            </Link>
          ))}
        </div>
      )}

      {selectedCategory && availableSubcategories.length > 0 && (
        <div className="filter-block">
          <div className="filter-title">Type</div>
          <Link to={pathFor(gender, categorySlug)} className={`filter-option ${!selectedSubcategory ? "filter-option-active" : ""}`}>
            All
          </Link>
          {availableSubcategories.map((sub) => (
            <Link
              key={sub}
              to={pathFor(gender, categorySlug, slugify(sub))}
              className={`filter-option ${selectedSubcategory === sub ? "filter-option-active" : ""}`}
            >
              {sub}
            </Link>
          ))}
        </div>
      )}

      {sizeLabels.length > 0 && (
        <div className="filter-block">
          <div className="filter-title">Age</div>
          {sizeLabels.map((size) => (
            <label key={size} className="filter-option">
              <input type="checkbox" checked={selectedSizes.includes(size)} onChange={() => toggleSize(size)} />
              {size}
            </label>
          ))}
        </div>
      )}

      {activeFilterCount > 0 && (
        <button className="btn btn-outline btn-sm" onClick={() => { navigate(pathFor(gender)); setSelectedSizes([]); }}>
          Clear filters
        </button>
      )}
    </>
  );

  const pageTitle = [selectedSubcategory, selectedCategory, gender && gender[0].toUpperCase() + gender.slice(1), "Ritu Fashion Store"]
    .filter(Boolean)
    .join(" | ");

  // Canonical strips the ?age= filter — that's a refinement of the same
  // page, not a distinct page, and shouldn't split ranking signal from it.
  const canonicalPath = ["/shop", gender, categorySlug, subcategorySlug].filter(Boolean).join("/");
  const canonicalUrl = `https://ritufashionstore.in${canonicalPath}`;

  const genderLabel = gender ? `${gender[0].toUpperCase()}${gender.slice(1)}` : "kids";
  const introText = selectedSubcategory
    ? `Shop ${selectedSubcategory.toLowerCase()} for ${genderLabel.toLowerCase()} in Gurgaon — soft cotton and festive styles with 1-day delivery.`
    : selectedCategory
    ? `Shop ${selectedCategory.toLowerCase()} for ${genderLabel.toLowerCase()} in Gurgaon — handpicked styles with 1-day delivery.`
    : gender
    ? `Shop ${genderLabel.toLowerCase()}' clothing in Gurgaon — everyday cotton and festive favourites, delivered in a day.`
    : "Everyday cotton and festive favourites for little ones, from our store in Gurgaon.";

  return (
    <div className="container section" style={{ paddingLeft: 16, paddingRight: 16, boxSizing: "border-box" }}>
      <Helmet>
        <title>{pageTitle || "Shop Kids Clothing | Ritu Fashion Store"}</title>
        <meta
          name="description"
          content={`Browse ${selectedSubcategory || selectedCategory || "cotton dresses, ethnic wear and party frocks"} for kids at Ritu Fashion Store, Gurgaon.`}
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <h1 style={{ fontSize: "2rem", fontWeight: 800 }}>{selectedSubcategory || selectedCategory || "Shop"}</h1>
      <p className="muted mt-2">{introText}</p>

      {/* Gender tabs */}
      <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
        {GENDERS.map((g) => (
          <Link
            key={g.value}
            to={g.value ? `/shop/${g.value}` : "/shop"}
            className={`btn btn-sm ${gender === g.value ? "btn-primary" : "btn-outline"}`}
          >
            {g.label}
          </Link>
        ))}
      </div>

      {/* Mobile-only filter trigger */}
      <button className="mobile-filter-trigger mt-4" onClick={() => setShowFilterSheet(true)}>
        ⚙️ Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
      </button>

      <div className="shop-layout mt-6">
        <aside className="shop-filters-desktop">{filterGroups}</aside>

        <div>
          {loading ? (
            <p className="muted">Loading…</p>
          ) : products.length === 0 ? (
            <p className="muted mt-8">Nothing here yet — new styles land every week.</p>
          ) : (
            <div className="product-grid">
              {products.map((p) => (
                <ProductCard key={p.productId} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showFilterSheet && (
        <div className="checkout-backdrop" onClick={() => setShowFilterSheet(false)}>
          <div className="checkout-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-sheet-handle" />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ fontSize: "1.125rem", fontWeight: 700, margin: 0 }}>Filters</h2>
              <button onClick={() => setShowFilterSheet(false)} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div className="mt-4">{filterGroups}</div>
            <button className="checkout-pay-btn mt-6" onClick={() => setShowFilterSheet(false)}>
              <span>Show {products.length} result{products.length !== 1 ? "s" : ""}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}